import { SignWebLocaleContext } from "@skyltmax/admin-ui"
import { type EpicViteContext, type renderFunc } from "@skyltmax/epic_vite"
import { isLocale } from "@skyltmax/locales"
import React, { useEffect, useState } from "react"
import { EpicViteCustomContext } from "@/admin/containers/contexts"
import { ApolloProviderPreloader } from "@/api/client"
import ErrorBoundary from "@/common/components/error_boundary"
import growthbook from "@/common/growthbook"
import { GrowthBookProvider } from "@/common/growthbook-react"
import i18n from "@/common/locales"
import { SentryContext } from "@/common/sentry"
import SentryReact from "@/common/sentry_client"
import { type SkyltmaxViteContext } from "@/common/vite_context"

interface EpicViteCustomContextWrapperProps {
  context?: SkyltmaxViteContext
  children: React.ReactNode
}

const EpicViteCustomContextWrapper: React.FC<EpicViteCustomContextWrapperProps> = ({ context, children }) => {
  const [customContext, setCustomContext] = useState(context || {})
  const [signWebLocale, setSignWebLocale] = useState(context?.signwebLocale || "en-IE")

  useEffect(() => {
    const handler = () => {
      const newLocale = sessionStorage.getItem("signweb_locale")

      if (newLocale && isLocale(newLocale)) {
        setSignWebLocale(newLocale)
      }
    }

    document.addEventListener("signweb_locale", handler)

    return () => {
      document.removeEventListener("signweb_locale", handler)
    }
  }, [])

  return (
    <SignWebLocaleContext.Provider value={{ signWebLocale, setSignWebLocale }}>
      <EpicViteCustomContext.Provider value={{ context: customContext, setContext: setCustomContext }}>
        {children}
      </EpicViteCustomContext.Provider>
    </SignWebLocaleContext.Provider>
  )
}

const WrappedApp = (
  Component: React.ComponentType<EpicViteContext<any>>,
  context: EpicViteContext<any, SkyltmaxViteContext>
) => {
  return (
    <React.StrictMode>
      <SentryContext.Provider value={SentryReact}>
        <EpicViteCustomContextWrapper context={context.customContext}>
          <React.Suspense fallback={null}>
            <ErrorBoundary>
              <GrowthBookProvider growthbook={growthbook}>
                <ApolloProviderPreloader {...context.props}>
                  <Component {...context} />
                </ApolloProviderPreloader>
              </GrowthBookProvider>
            </ErrorBoundary>
          </React.Suspense>
        </EpicViteCustomContextWrapper>
      </SentryContext.Provider>
    </React.StrictMode>
  )
}

export const appShell = <TProps,>(
  Component: React.ComponentType<EpicViteContext<TProps>>,
  preHook?: () => void
): renderFunc<TProps> => {
  const WrappedRenderFunc: renderFunc<any, SkyltmaxViteContext> = context => {
    if (i18n.language !== context.locale) {
      i18n.changeLanguage(context.locale)
    }

    if (context.customContext?.flags) {
      growthbook.initSync({
        payload: {
          features: {
            ...context.customContext.flags,
            ...(context.customContext.overrides || {}),
          },
        },
      })
    }

    if (context.customContext?.flagsAttributes) {
      growthbook.setAttributes(context.customContext.flagsAttributes)
    }

    if (preHook) {
      preHook()
    }

    return WrappedApp(Component, context)
  }

  return WrappedRenderFunc
}

const WrappedComponent = (Component: React.ComponentType<any>, props?: any, context?: SkyltmaxViteContext) => {
  return (
    <SentryContext.Provider value={SentryReact}>
      <EpicViteCustomContextWrapper context={context}>
        <React.Suspense fallback={null}>
          <ErrorBoundary>
            <GrowthBookProvider growthbook={growthbook}>
              <ApolloProviderPreloader {...props}>
                <Component {...props} />
              </ApolloProviderPreloader>
            </GrowthBookProvider>
          </ErrorBoundary>
        </React.Suspense>
      </EpicViteCustomContextWrapper>
    </SentryContext.Provider>
  )
}

const componentShell = <TProps,>(Component: React.ComponentType<TProps>, preHook?: () => void): renderFunc<TProps> => {
  const WrappedRenderFunc: renderFunc<any, SkyltmaxViteContext> = context => {
    if (i18n.language !== context.locale) {
      i18n.changeLanguage(context.locale)
    }

    if (context.customContext?.flags) {
      growthbook.initSync({
        payload: {
          features: {
            ...context.customContext.flags,
            ...(context.customContext.overrides || {}),
          },
        },
      })
    }

    if (context.customContext?.flagsAttributes) {
      growthbook.setAttributes(context.customContext.flagsAttributes)
    }

    if (preHook) {
      preHook()
    }

    return WrappedComponent(Component, context.props, context.customContext)
  }

  return WrappedRenderFunc
}

export default componentShell
